(() => {
  const elementName = "monsga-video-gallery";
  if (customElements.get(elementName)) return;

  class MonsgaVideoGallery extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.track = this.querySelector("[data-video-track]");
      this.dialog = this.querySelector("[data-video-dialog]");
      this.dialogBody = this.querySelector("[data-video-dialog-body]");
      this.dialogTitle = this.querySelector("[data-video-dialog-title]");
      this.dialogLink = this.querySelector("[data-video-dialog-link]");
      this.activeTitle = this.querySelector("[data-carousel-active-title]");
      this.activeDescription = this.querySelector("[data-carousel-active-description]");
      this.originalCards = Array.from(this.track?.querySelectorAll(":scope > [data-video-card]") || []);
      this.loopEnabled = this.track?.dataset.loop === "true" && this.originalCards.length > 1;
      this.isIntersecting = !("IntersectionObserver" in window);

      if (this.loopEnabled) this.setupLoop();
      this.cards = Array.from(this.track?.querySelectorAll(":scope > [data-video-card]") || []);
      this.cards.forEach((card) => this.bindCard(card));
      this.bindMouseDrag();

      this.querySelector("[data-video-prev]")?.addEventListener("click", () => this.move(-1));
      this.querySelector("[data-video-next]")?.addEventListener("click", () => this.move(1));
      this.querySelectorAll("[data-video-dot]").forEach((dot) => {
        dot.addEventListener("click", () => this.goTo(Number(dot.dataset.videoDot)));
      });
      this.querySelector("[data-video-close]")?.addEventListener("click", () => this.close());
      this.dialog?.addEventListener("click", (event) => { if (event.target === this.dialog) this.close(); });
      this.dialog?.addEventListener("close", () => this.clearDialog());

      this.track?.addEventListener("scroll", () => {
        this.updateActive();
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => this.normalizeLoop(), 140);
      }, { passive: true });

      this.handleResize = () => {
        if (!this.loopEnabled) return;
        const activeIndex = Number(this.getClosestCard()?.dataset.carouselIndex || 0);
        this.measureLoop();
        this.goTo(activeIndex, "auto");
      };
      addEventListener("resize", this.handleResize, { passive: true });

      if ("IntersectionObserver" in window) {
        this.visibilityObserver = new IntersectionObserver(([entry]) => {
          this.isIntersecting = entry.isIntersecting;
          this.syncPreviewPlayback(this.getClosestCard());
        }, { threshold: 0.15 });
        this.visibilityObserver.observe(this);
      }

      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (this.loopEnabled) {
          this.measureLoop();
          this.track.scrollLeft = this.loopSpan;
        }
        this.updateActive();
      }));
    }

    disconnectedCallback() {
      removeEventListener("resize", this.handleResize);
      this.visibilityObserver?.disconnect();
      this.cards?.forEach((card) => card.querySelector("video")?.pause());
      clearTimeout(this.scrollTimer);
      this.unbindMouseDrag();
    }

    bindMouseDrag() {
      if (!this.track) return;
      this.dragState = null;
      this.handlePointerDown = (event) => {
        if (event.pointerType !== "mouse" || event.button !== 0) return;
        if (event.target.closest("a,button,input,select,textarea")) return;
        this.dragState = { pointerId: event.pointerId, startX: event.clientX, startScrollLeft: this.track.scrollLeft, moved: false };
      };
      this.handlePointerMove = (event) => {
        const drag = this.dragState;
        if (!drag || event.pointerId !== drag.pointerId) return;
        const delta = event.clientX - drag.startX;
        if (!drag.moved && Math.abs(delta) <= 4) return;
        if (!drag.moved) {
          drag.moved = true;
          this.track.classList.add("is-dragging");
          this.track.setPointerCapture?.(event.pointerId);
        }
        this.track.scrollLeft = drag.startScrollLeft - delta;
        event.preventDefault();
      };
      this.handlePointerEnd = (event) => {
        const drag = this.dragState;
        if (!drag || event.pointerId !== drag.pointerId) return;
        this.dragState = null;
        this.track.classList.remove("is-dragging");
        if (this.track.hasPointerCapture?.(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
        if (drag.moved) {
          this.dragMoved = true;
          setTimeout(() => { this.dragMoved = false; }, 0);
        }
      };
      this.handleTrackDragStart = (event) => event.preventDefault();
      this.track.addEventListener("pointerdown", this.handlePointerDown);
      this.track.addEventListener("pointermove", this.handlePointerMove);
      this.track.addEventListener("pointerup", this.handlePointerEnd);
      this.track.addEventListener("pointercancel", this.handlePointerEnd);
      this.track.addEventListener("dragstart", this.handleTrackDragStart);
    }

    unbindMouseDrag() {
      if (!this.track || !this.handlePointerDown) return;
      this.track.removeEventListener("pointerdown", this.handlePointerDown);
      this.track.removeEventListener("pointermove", this.handlePointerMove);
      this.track.removeEventListener("pointerup", this.handlePointerEnd);
      this.track.removeEventListener("pointercancel", this.handlePointerEnd);
      this.track.removeEventListener("dragstart", this.handleTrackDragStart);
      this.dragState = null;
    }

    setupLoop() {
      const before = document.createDocumentFragment();
      const after = document.createDocumentFragment();

      this.originalCards.forEach((card) => {
        const beforeClone = card.cloneNode(true);
        const afterClone = card.cloneNode(true);
        [beforeClone, afterClone].forEach((clone) => {
          [clone, ...clone.querySelectorAll("*")].forEach((node) => {
            node.removeAttribute?.("id");
            Array.from(node.attributes || []).forEach((attribute) => {
              if (attribute.name.startsWith("data-shopify")) node.removeAttribute(attribute.name);
            });
          });
        });
        beforeClone.dataset.loopCopy = "before";
        afterClone.dataset.loopCopy = "after";
        beforeClone.setAttribute("aria-hidden", "true");
        afterClone.setAttribute("aria-hidden", "true");
        beforeClone.tabIndex = -1;
        afterClone.tabIndex = -1;
        beforeClone.querySelectorAll("video").forEach((video) => { video.preload = "metadata"; });
        afterClone.querySelectorAll("video").forEach((video) => { video.preload = "metadata"; });
        before.append(beforeClone);
        after.append(afterClone);
      });

      this.track.prepend(before);
      this.track.append(after);
    }

    measureLoop() {
      if (!this.track || !this.originalCards.length) return;
      const gap = Number.parseFloat(getComputedStyle(this.track).columnGap || 0);
      this.loopSpan = this.originalCards.reduce((total, card) => total + card.getBoundingClientRect().width + gap, 0);
    }

    bindCard(card) {
      card.querySelectorAll("a").forEach((link) => link.addEventListener("click", (event) => event.stopPropagation()));
      const preview = card.querySelector("video");
      const externalId = card.dataset.videoExternalId;
      const externalType = card.dataset.videoExternalType;
      const playButton = card.querySelector(".mh-social__play");
      if (preview && matchMedia("(hover:hover) and (pointer:fine)").matches) {
        card.addEventListener("mouseenter", () => {
          preview.muted = true;
          preview.play().then(() => playButton?.setAttribute("hidden", "")).catch(() => {});
        });
        card.addEventListener("mouseleave", () => {
          if (this.dataset.carouselKind === "technology" && card.classList.contains("is-active") && this.isIntersecting) return;
          preview.pause();
          preview.currentTime = 0;
          playButton?.removeAttribute("hidden");
        });
      }
      if (externalId && matchMedia("(hover:hover) and (pointer:fine)").matches) {
        card.addEventListener("mouseenter", () => this.startExternalPreview(card, externalId, externalType));
        card.addEventListener("mouseleave", () => this.stopExternalPreview(card));
      }
      card.addEventListener("click", (event) => {
        if (event.target.closest("a")) return;
        if (this.dragMoved) return;
        this.open(card);
      });
      card.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        this.open(card);
      });
    }

    startExternalPreview(card, id, type) {
      if (card.querySelector(".mh-social__hover-video")) return;
      const frame = document.createElement("iframe");
      frame.className = "mh-social__hover-video";
      frame.title = card.dataset.videoTitle || "Video preview";
      frame.allow = "autoplay; encrypted-media; picture-in-picture";
      frame.setAttribute("aria-hidden", "true");
      if (type === "vimeo") {
        frame.src = `https://player.vimeo.com/video/${encodeURIComponent(id)}?autoplay=1&muted=1&background=1&loop=1`;
      } else {
        frame.src = `https://www.youtube.com/embed/${encodeURIComponent(id)}?autoplay=1&mute=1&controls=0&rel=0&loop=1&playlist=${encodeURIComponent(id)}&playsinline=1`;
      }
      frame.addEventListener("load", () => {
        card.classList.add("is-previewing");
        card.querySelector(".mh-social__play")?.setAttribute("hidden", "");
      }, { once: true });
      card.querySelector(".mh-social__media")?.append(frame);
    }

    stopExternalPreview(card) {
      card.querySelector(".mh-social__hover-video")?.remove();
      card.classList.remove("is-previewing");
      card.querySelector(".mh-social__play")?.removeAttribute("hidden");
    }

    getClosestCard() {
      if (!this.track || !this.cards?.length) return null;
      const trackRect = this.track.getBoundingClientRect();
      const center = trackRect.left + trackRect.width / 2;
      return this.cards.reduce((closest, card) => {
        const rect = card.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - center);
        return !closest || distance < closest.distance ? { card, distance } : closest;
      }, null)?.card;
    }

    updateActive() {
      const activeCard = this.getClosestCard();
      if (!activeCard) return;
      this.cards.forEach((card) => card.classList.toggle("is-active", card === activeCard));
      const activeIndex = Number(activeCard.dataset.carouselIndex || 0);
      if (this.activeTitle) this.activeTitle.textContent = activeCard.dataset.videoTitle || "";
      if (this.activeDescription) this.activeDescription.textContent = activeCard.dataset.videoDescription || "";
      this.querySelectorAll("[data-video-dot]").forEach((dot) => {
        const isActive = Number(dot.dataset.videoDot) === activeIndex;
        dot.classList.toggle("is-active", isActive);
        dot.setAttribute("aria-current", String(isActive));
      });
      this.syncPreviewPlayback(activeCard);
    }

    syncPreviewPlayback(activeCard) {
      if (this.dataset.carouselKind !== "technology") return;
      this.cards.forEach((card) => {
        const video = card.querySelector(".mh-tech__media video");
        if (!video) return;
        if (card === activeCard && this.isIntersecting) {
          video.muted = true;
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    }

    normalizeLoop() {
      if (!this.loopEnabled || !this.loopSpan) return;
      const activeCard = this.getClosestCard();
      const copy = activeCard?.dataset.loopCopy;
      if (!copy) return;
      this.track.style.scrollSnapType = "none";
      this.track.scrollLeft += copy === "before" ? this.loopSpan : -this.loopSpan;
      requestAnimationFrame(() => {
        this.track.style.removeProperty("scroll-snap-type");
        this.updateActive();
      });
    }

    move(direction) {
      const card = this.getClosestCard() || this.originalCards[0];
      if (!card || !this.track) return;
      const gap = Number.parseFloat(getComputedStyle(this.track).columnGap || 0);
      const behavior = matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
      this.track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior });
    }

    goTo(index, behavior = "smooth") {
      const card = this.originalCards[index];
      if (!card || !this.track) return;
      const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)").matches;
      const left = card.offsetLeft - (this.track.clientWidth - card.getBoundingClientRect().width) / 2;
      this.track.scrollTo({ left, behavior: reducedMotion ? "auto" : behavior });
    }

    open(card) {
      if (!this.dialog || !this.dialogBody) return;
      this.stopExternalPreview(card);
      const preview = card.querySelector(".mh-social__media > video");
      if (preview) {
        preview.pause();
        preview.currentTime = 0;
        card.querySelector(".mh-social__play")?.removeAttribute("hidden");
      }
      this.lastTrigger = card;
      this.dialogTitle.textContent = card.dataset.videoTitle || "Video";
      if (this.dialogLink) {
        const externalId = card.dataset.videoExternalId || "";
        const externalUrl = card.dataset.videoExternalType === "youtube" && externalId
          ? `https://www.youtube.com/watch?v=${encodeURIComponent(externalId)}`
          : "";
        const sourceUrl = card.dataset.videoSourceUrl || externalUrl;
        this.dialogLink.href = sourceUrl || "#";
        this.dialogLink.dataset.visible = String(Boolean(sourceUrl));
        this.dialogLink.style.display = sourceUrl ? "inline-flex" : "none";
      }
      const template = card.querySelector("template[data-video-modal]");
      if (template) this.dialogBody.replaceChildren(template.content.cloneNode(true));
      else {
        const image = card.querySelector("img")?.cloneNode(true);
        if (image) this.dialogBody.replaceChildren(image);
      }
      if (this.dialog.showModal) this.dialog.showModal(); else this.dialog.setAttribute("open", "");
      const video = this.dialogBody.querySelector("video");
      if (video) video.play().catch(() => {});
    }

    close() {
      if (!this.dialog) return;
      if (this.dialog.close && this.dialog.open) this.dialog.close(); else { this.dialog.removeAttribute("open"); this.clearDialog(); }
      this.lastTrigger?.focus?.();
    }

    clearDialog() {
      this.dialogBody?.querySelectorAll("video").forEach((video) => video.pause());
      this.dialogBody?.replaceChildren();
    }
  }

  customElements.define(elementName, MonsgaVideoGallery);
})();
